import React, { useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AssessmentIcon from '@mui/icons-material/Assessment'
import GavelIcon from '@mui/icons-material/Gavel'
import DescriptionIcon from '@mui/icons-material/Description'
import { getReportTypes, getReportStatusByType } from 'services/reportingService'
import { useSelector } from 'react-redux'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import LocalStorageService from 'services/LocalStorageService'
import { KEYS } from 'utils/localStorage'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import BpAuditTab from './BpAuditTab'

const TYPE_ICONS = {
  insurance: AssessmentIcon,
  bscr: HealthAndSafetyIcon,
}

// Slug → category: cpseX → commercial, bscr → safety, else insurance.
const getCategory = (slug) => {
  if (String(slug).startsWith('cpse')) return 'commercial'
  if (slug === 'bscr') return 'safety'
  return 'insurance'
}

const TABS = [
  { id: 'insurance', label: 'Insurance' },
  { id: 'commercial', label: 'Commercial Enquiries (CPSE)' },
  { id: 'safety', label: 'Building Safety' },
  { id: 'audit', label: 'Compliance Audit' },
]

const VALID_TAB_IDS = TABS.map(t => t.id)

const ReportTypesPage = ({ onSelectType }) => {
  const bp = useSelector(getSelectedBp)
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [reportStatus, setReportStatus] = useState({})

  // Persist tab choice across visits. URL ?tab=… wins over localStorage so
  // deep-links (e.g. from the Wiki audit-history chip) can target a tab.
  const [activeTab, _setActiveTab] = useState(() => {
    const urlTab = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('tab')
      : null
    if (urlTab && VALID_TAB_IDS.includes(urlTab)) return urlTab
    const stored = LocalStorageService.get(KEYS.reportTypesTab)
    return VALID_TAB_IDS.includes(stored) ? stored : 'insurance'
  })
  const setActiveTab = (tab) => {
    _setActiveTab(tab)
    LocalStorageService.set(KEYS.reportTypesTab, tab)
  }

  useEffect(() => {
    getReportTypes()
      .then(res => setTypes(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!bp?.id) return
    // One batched request returns { [slug]: { total, latestStatus, latestVersion } }
    // instead of fanning out N paginated calls (one per report type).
    getReportStatusByType(bp.id)
      .then(res => setReportStatus(res.data.data || {}))
      .catch(() => {})
  }, [bp?.id])

  // Group types and track per-tab counts
  const grouped = useMemo(() => {
    const out = { insurance: [], commercial: [], safety: [] }
    types.forEach(t => { out[getCategory(t.slug)].push(t) })
    // Keep commercial enquiries in numeric CPSE order (cpse1 … cpse7)
    out.commercial.sort((a, b) => {
      const an = parseInt(String(a.slug).replace(/\D/g, ''), 10) || 0
      const bn = parseInt(String(b.slug).replace(/\D/g, ''), 10) || 0
      return an - bn
    })
    return out
  }, [types])

  // Default to whichever tab has entries — if the only type is insurance, start there
  useEffect(() => {
    if (!loading && grouped.commercial.length > 0 && grouped.insurance.length === 0) {
      setActiveTab('commercial')
    }
  }, [loading, grouped])

  if (loading) {
    return <LoadingState><CircularProgress size={28} sx={{ color: '#D24724' }} /></LoadingState>
  }

  const visible = grouped[activeTab] || []

  return (
    <Container>
      <Header>
        <Title>
          <TitleBar />
          Reports
        </Title>
        <TitleUnderline />
        <Subtitle>Generate AI-powered reports from your building's documents.</Subtitle>
      </Header>

      <Tabs>
        {TABS.map(t => {
          if (t.id === 'audit') {
            return (
              <Tab key={t.id} $active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
                <FactCheckIcon style={{ fontSize: 14 }} /> {t.label}
              </Tab>
            )
          }
          const count = grouped[t.id]?.length || 0
          if (count === 0) return null
          return (
            <Tab key={t.id} $active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
              {t.id === 'safety' && <HealthAndSafetyIcon style={{ fontSize: 14 }} />}
              {t.label}
              <TabCount>{count}</TabCount>
            </Tab>
          )
        })}
      </Tabs>

      {activeTab === 'audit' ? (
        <BpAuditTab bpId={bp?.id} bpName={bp?.name} />
      ) : (
      <TypeList>
        {visible.map(type => {
          const Icon = TYPE_ICONS[type.slug]
            || (getCategory(type.slug) === 'commercial' ? GavelIcon
              : getCategory(type.slug) === 'safety' ? HealthAndSafetyIcon
              : DescriptionIcon)
          const status = reportStatus[type.slug]

          return (
            <TypeCard key={type.id} onClick={() => onSelectType(type.slug)}>
              <TypeIcon>
                <Icon style={{ fontSize: 18 }} />
              </TypeIcon>
              <TypeBody>
                <TypeName>{type.name}</TypeName>
                <TypeDesc>{type.description}</TypeDesc>
                <TypeFooter>
                  <TypeMeta>{type.questionCount} questions</TypeMeta>
                  {status?.latestStatus === 'processing' && <StatusChip $processing>Generating...</StatusChip>}
                  {status?.latestStatus === 'pending' && <StatusChip $processing>Queued</StatusChip>}
                  {status?.latestStatus === 'completed' && status.total > 0 && (
                    <StatusChip $done>{status.total} report{status.total !== 1 ? 's' : ''}</StatusChip>
                  )}
                  {status?.latestStatus === 'failed' && <StatusChip $failed>Last run failed</StatusChip>}
                </TypeFooter>
              </TypeBody>
              <TypeArrow><ArrowForwardIcon style={{ fontSize: 16 }} /></TypeArrow>
            </TypeCard>
          )
        })}
      </TypeList>
      )}

      {activeTab !== 'audit' && visible.length === 0 && (
        <EmptyMsg>No report types in this category.</EmptyMsg>
      )}
    </Container>
  )
}

export default ReportTypesPage

const Container = styled.div`
  padding: 24px;
  @media (max-width: 768px) { padding: 16px; }
`

const Header = styled.div` margin-bottom: 20px; `

const Title = styled.h1`
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px;
`

const TitleBar = styled.span`
  display: inline-block;
  width: 4px;
  height: 18px;
  background: ${({ theme }) => theme.colors.primary};
  margin-right: 10px;
  border-radius: 2px;
`

const TitleUnderline = styled.div`
  width: 70px;
  height: 3px;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border-radius: 3px;
  margin-left: 14px;
`

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 10px 0 0 14px;
  line-height: 1.5;
`

const Tabs = styled.div`
  display: flex;
  gap: 2px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : (theme.colors.grey?.[800] || '#666')};
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? 600 : 500};
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`

const TabCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.grey?.[400] || '#f4f4f5'};
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
`

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  padding: 64px;
`

const TypeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TypeCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grey?.[500] || '#e6e6e6'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  align-items: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
`

const TypeIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #fbede9;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const TypeBody = styled.div`
  flex: 1;
  min-width: 0;
`

const TypeName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.3;
`

const TypeDesc = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 2px 0 0;
  line-height: 1.4;
`

const TypeFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`

const TypeMeta = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
`

const StatusChip = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 3px;
  color: ${({ $processing, $failed, $done }) =>
    $processing ? '#D24724' : $failed ? '#dc2626' : $done ? '#059669' : '#666'};
  background: ${({ $processing, $failed, $done }) =>
    $processing ? '#fbede9' : $failed ? '#fef2f2' : $done ? '#ecfdf5' : '#f9fafb'};
`

const TypeArrow = styled.div`
  color: ${({ theme }) => theme.colors.grey?.[600] || '#ccc'};
  transition: color 0.15s;
  flex-shrink: 0;

  ${TypeCard}:hover & { color: ${({ theme }) => theme.colors.primary}; }
`

const EmptyMsg = styled.div`
  text-align: center;
  padding: 48px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  font-size: 14px;
`
